/**
 * Định dạng số, ngày và dung lượng (§13.5 của spec UI).
 *
 * ## Vì sao số đi theo ngôn ngữ mà ngày thì không
 *
 * **Số**: `1.234.567` ở tiếng Việt và `1,234,567` ở tiếng Anh — dấu phân nhóm
 * đảo nghĩa giữa hai locale, nên để nguyên `vi-VN` khi đang xem tiếng Anh thì
 * `1.234` đọc thành "một phẩy hai ba tư". Đây là sai số thật, phải đi theo
 * ngôn ngữ.
 *
 * **Ngày**: giữ `dd/MM/yyyy` ở **cả hai** ngôn ngữ. Đặc tả chốt dạng này, và
 * locale `en-GB` cũng cho đúng dạng đó — khác `en-US`, nơi `03/04` đảo thành
 * tháng Ba ngày Tư. Hàm `ngayVN` vẫn tự tách chuỗi nên không phụ thuộc locale.
 *
 * Hàm ở đây là **hàm thuần**, không gọi hook, nên nhận `locale` qua tham số.
 * Chỗ dùng lấy `locale` từ `useT()`. Bỏ trống thì về `vi-VN` — mặc định an
 * toàn cho những chỗ gọi ngoài vòng React (tầng repository, hàm tiện ích).
 */
import { MA_LOCALE } from '../i18n/config';

/** Ô không có dữ liệu dùng dấu gạch dài, nhất quán ở mọi bảng và mọi ngôn ngữ. */
export const KHONG_CO = '—';

const MAC_DINH = MA_LOCALE.vi;

/** `55010` → `55.010` (vi) hoặc `55,010` (en). */
export function soVN(n: number | null | undefined, locale: string = MAC_DINH): string {
  if (n === null || n === undefined) return KHONG_CO;
  return n.toLocaleString(locale);
}

/**
 * Ngày dạng `YYYY-MM-DD` phải **tách chuỗi**, không đưa qua `new Date()`.
 *
 * Chuỗi chỉ có ngày bị hiểu là nửa đêm UTC; ở múi giờ Asia/Ho_Chi_Minh (UTC+7)
 * thì `new Date('2026-01-15')` cho ra 15/01 lúc 07:00 — nhưng với múi giờ âm nó
 * lùi hẳn sang ngày hôm trước. Tách chuỗi thì không bao giờ lệch.
 *
 * Dạng `dd/MM/yyyy` ở cả hai ngôn ngữ, nên hàm này **không** nhận `locale`.
 */
export function ngayVN(iso: string | null | undefined): string {
  if (!iso) return KHONG_CO;
  const phanNgay = iso.slice(0, 10);
  const [y, m, d] = phanNgay.split('-');
  if (!y || !m || !d) return KHONG_CO;
  return `${d}/${m}/${y}`;
}

/**
 * Ngày kèm giờ. Chuỗi có phần giờ thì mới dùng `Date` được an toàn.
 *
 * Giờ dùng `hour12: false` ở cả hai ngôn ngữ: nhật ký vận hành đọc theo 24 giờ,
 * và `3:05` không nói rõ sáng hay chiều.
 */
export function ngayGioVN(
  iso: string | null | undefined,
  locale: string = MAC_DINH,
): string {
  if (!iso) return KHONG_CO;
  // Không có phần giờ thì chỉ hiện ngày, đừng bịa ra 00:00.
  if (!iso.includes('T')) return ngayVN(iso);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return KHONG_CO;
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const DON_VI_DUNG_LUONG = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/**
 * Dung lượng theo bậc 1024: `10737418240` → `10 GB`, `1610612736` → `1,5 GB`
 * (vi) hoặc `1.5 GB` (en). Đơn vị là ký hiệu, không dịch.
 */
export function dungLuongVN(
  bytes: number | null | undefined,
  locale: string = MAC_DINH,
): string {
  if (bytes === null || bytes === undefined) return KHONG_CO;
  let giaTri = bytes;
  let bac = 0;
  while (giaTri >= 1024 && bac < DON_VI_DUNG_LUONG.length - 1) {
    giaTri /= 1024;
    bac += 1;
  }
  return `${giaTri.toLocaleString(locale, { maximumFractionDigits: 1 })} ${DON_VI_DUNG_LUONG[bac]}`;
}
